import type { AppState } from '../../store/app-state';
import type { CloudState } from '../../store/cloud-types';
import type { ProjectScope } from './project-scope';
import { applyProjectMemberPersonnelLinks } from './project-reader';
import {
    applySubscriptionControlFields,
    type RemoteProjectUpdateWithControls,
} from './subscription-control-fields';
import {
    clearPendingPropDeletesMissingFromRemote,
    clearPendingPropWrite,
    isPendingPropDelete,
    isPendingPropWrite,
} from './prop-pending-writes';
import {
    clearPendingCueAnchorDeletesMissingFromRemote,
    clearPendingCueAnchorWrite,
    clearPendingCustomCueWrite,
    isPendingCueAnchorDelete,
    isPendingCueAnchorWrite,
    isPendingCustomCueWrite,
} from './prompt-book-pending-writes';
import {
    clearPendingSetDeletesMissingFromRemote,
    clearPendingSetWrite,
    isPendingSetDelete,
    isPendingSetWrite,
} from './set-pending-writes';
import {
    clearPendingFlyCueWrite,
    isPendingFlyCueWrite,
} from './fly-pending-writes';
import {
    clearPendingProjectionCueWrite,
    isPendingProjectionCueWrite,
} from './projection-pending-writes';
import {
    clearPendingMicAssignmentDeletesMissingFromRemote,
    clearPendingMicAssignmentWrite,
    clearPendingMicPackConfigWrite,
    clearPendingMicSwapDeletesMissingFromRemote,
    clearPendingMicSwapWrite,
    clearPendingSoundClipDeletesMissingFromRemote,
    clearPendingSoundClipWrite,
    clearPendingSoundCueDeletesMissingFromRemote,
    clearPendingSoundCueWrite,
    isPendingMicAssignmentDelete,
    isPendingMicAssignmentWrite,
    isPendingMicPackConfigWrite,
    isPendingMicSwapDelete,
    isPendingMicSwapWrite,
    isPendingSoundClipDelete,
    isPendingSoundClipWrite,
    isPendingSoundCueDelete,
    isPendingSoundCueWrite,
} from './sound-pending-writes';
import {
    clearPendingFollowSpotCueWrite,
    clearPendingFollowSpotDeletesMissingFromRemote,
    clearPendingFollowSpotWrite,
    clearPendingLightingCueWrite,
    isPendingFollowSpotCueWrite,
    isPendingFollowSpotDelete,
    isPendingFollowSpotWrite,
    isPendingLightingCueWrite,
} from './lighting-pending-writes';
import {
    CLOUD_BASELINE_PROJECT_DATA_RESOURCES,
    GLOBAL_PROJECT_DATA_RESOURCES,
} from './project-data-requirements';
import {
    clearPendingPersonnelProfileDeletesMissingFromRemote,
    isPendingPersonnelProfileDelete,
} from './personnel-pending-writes.ts';
type CloudConnectionState = AppState['cloud'];
type AppStateSnapshot = AppState;
type AppProjectMeta = AppStateSnapshot['projectMeta'];
type AppShowStructure = AppStateSnapshot['showStructure'];
type AppScriptBlocks = AppShowStructure['scriptBlocks'];
type AppPersonnel = NonNullable<CloudState['personnel']>[number];
type AppProp = NonNullable<CloudState['props']>[number];
type AppSet = NonNullable<CloudState['sets']>[number];
type AppSoundClip = NonNullable<CloudState['soundClips']>[number];
type AppSoundCue = NonNullable<CloudState['soundCues']>[number];
type AppMicAssignment = NonNullable<CloudState['micAssignments']>[number];
type AppMicSwap = NonNullable<CloudState['micSwaps']>[number];
type AppMicPackConfig = NonNullable<CloudState['micPackConfig']>;
type AppCueAnchor = NonNullable<CloudState['cueAnchors']>[number];
type AppCustomCue = NonNullable<CloudState['customCues']>[number];
type AppFlyCue = NonNullable<CloudState['flyCues']>[number];
type AppLightingCue = NonNullable<CloudState['lightingCues']>[number];
type AppFollowSpotCue = NonNullable<CloudState['followSpotCues']>[number];
type AppFollowSpot = NonNullable<CloudState['followSpots']>[number];
type AppProjectionCue = NonNullable<CloudState['projectionCues']>[number];

const PENDING_GLOBAL_CLOUD_FIELDS = [
    'projectMeta',
    'showStructure',
    'rows',
    'personnel',
    'castAssignments',
] as const;
export type PendingGlobalCloudField = typeof PENDING_GLOBAL_CLOUD_FIELDS[number];
const pendingGlobalCloudFieldLookup: ReadonlySet<string> = new Set(PENDING_GLOBAL_CLOUD_FIELDS);

export function hasLoadedInitialCloudResources(loaded: ReadonlySet<string>): boolean {
    return GLOBAL_PROJECT_DATA_RESOURCES.every(resourceId => loaded.has(resourceId));
}

export function createLoadedResourcesForCloudBaseline(
    authoritativeResourceIds: readonly string[] = CLOUD_BASELINE_PROJECT_DATA_RESOURCES,
): Set<string> {
    return new Set(authoritativeResourceIds);
}

export function getCloudAutosaveLoadedResources(
    loaded: ReadonlySet<string>,
    hasLoadedCloudProject: boolean,
    authoritativeResourceIds?: readonly string[],
): ReadonlySet<string> {
    if (!hasLoadedCloudProject) return loaded;
    const baselineResourceIds = authoritativeResourceIds ?? CLOUD_BASELINE_PROJECT_DATA_RESOURCES;
    return baselineResourceIds.every(resourceId => loaded.has(resourceId))
        ? loaded
        : createLoadedResourcesForCloudBaseline(baselineResourceIds);
}

export function shouldSkipCloudAutosaveForMetaUpdate(
    isUpdatingCloudMetadata: boolean,
    hasCloudChanges: boolean,
): boolean {
    return isUpdatingCloudMetadata && !hasCloudChanges;
}

export function shouldSkipCloudAutosaveForRemoteUpdate(isApplyingRemoteSnapshot: boolean): boolean {
    return isApplyingRemoteSnapshot;
}

export function shouldSkipQueuedCloudAutosaveForRemoteVersion(state: {
    currentRemoteVersion: number;
    versionAtSchedule: number;
    hasPendingLocalAutosave: boolean;
}): boolean {
    return state.currentRemoteVersion !== state.versionAtSchedule
        && !state.hasPendingLocalAutosave;
}

export function shouldApplyRemoteSnapshotForCloudAuthority(state: {
    hasPendingAutosave: boolean;
    isSaving: boolean;
}): boolean {
    void state;
    return true;
}

function normalizePendingComparisonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalizePendingComparisonValue);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([, entryValue]) => entryValue !== undefined)
                .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
                .map(([key, entryValue]) => [key, normalizePendingComparisonValue(entryValue)])
        );
    }
    return value;
}

function recordsMatchForPendingWrite(left: unknown, right: unknown): boolean {
    return JSON.stringify(normalizePendingComparisonValue(left))
        === JSON.stringify(normalizePendingComparisonValue(right));
}

export function getPendingGlobalCloudFieldsForAutosave(
    changedKeys: readonly PropertyKey[],
    currentState?: Pick<AppStateSnapshot, 'showStructure'>,
    previousState?: Pick<AppStateSnapshot, 'showStructure'>,
): PendingGlobalCloudField[] {
    return changedKeys.filter((key): key is PendingGlobalCloudField => (
        typeof key === 'string' && pendingGlobalCloudFieldLookup.has(key)
    )).filter(field => {
        if (field !== 'showStructure' || !currentState || !previousState) {
            return true;
        }
        return !recordsMatchForPendingWrite(
            cloudComparableShowStructure(currentState.showStructure),
            cloudComparableShowStructure(previousState.showStructure),
        );
    });
}

export function mergeGlobalCloudFieldWithPendingLocal<T>(
    pendingFields: ReadonlySet<string>,
    field: PendingGlobalCloudField,
    remoteValue: T,
    localValue: T
): T {
    return pendingFields.has(field) ? localValue : remoteValue;
}

function mergePersonnelWithPendingLocal(
    scope: ProjectScope,
    remotePersonnel: AppPersonnel[]
): AppPersonnel[] {
    return remotePersonnel.filter(person => !isPendingPersonnelProfileDelete(scope, person.id));
}

export function clearAcknowledgedPendingGlobalCloudField<T>(
    pendingFields: Set<string>,
    field: PendingGlobalCloudField,
    remoteValue: T,
    localValue: T
): void {
    if (recordsMatchForPendingWrite(remoteValue, localValue)) {
        pendingFields.delete(field);
    }
}

export function cloudComparableShowStructure(showStructure: AppShowStructure): Omit<AppShowStructure, 'scriptBlocks'> {
    const { scriptBlocks, ...showStructureWithoutBlocks } = showStructure;
    void scriptBlocks;
    return showStructureWithoutBlocks;
}

export function projectMetaComparableForCloudSnapshot(
    remoteMeta: Partial<AppProjectMeta>,
    localMeta: AppProjectMeta,
): Partial<AppProjectMeta> {
    const comparable: Record<string, unknown> = {};
    for (const key of Object.keys(remoteMeta)) {
        comparable[key] = localMeta[key as keyof AppProjectMeta];
    }
    return comparable as Partial<AppProjectMeta>;
}

export function mergePropsWithPendingLocal(
    scope: ProjectScope,
    remoteProps: AppProp[],
    localProps: AppProp[]
): AppProp[] {
    const remoteIds = new Set(remoteProps.map(prop => prop.id));
    const localById = new Map(localProps.map(prop => [prop.id, prop]));
    const visibleRemoteProps = remoteProps
        .filter(prop => !isPendingPropDelete(scope, prop.id))
        .map(prop => {
            const localProp = localById.get(prop.id);
            if (localProp && isPendingPropWrite(scope, prop.id)) {
                return localProp;
            }
            return prop;
        });
    const pendingLocalProps = localProps.filter(prop => (
        !remoteIds.has(prop.id) && isPendingPropWrite(scope, prop.id)
    ));

    return [...visibleRemoteProps, ...pendingLocalProps];
}

function clearAcknowledgedPendingPropWrites(
    scope: ProjectScope,
    remoteProps: AppProp[],
    localProps: AppProp[]
): void {
    const localById = new Map(localProps.map(prop => [prop.id, prop]));
    const remotePropIds = new Set(remoteProps.map(prop => prop.id));

    for (const remoteProp of remoteProps) {
        const localProp = localById.get(remoteProp.id);
        if (!localProp || recordsMatchForPendingWrite(remoteProp, localProp)) {
            clearPendingPropWrite(scope, remoteProp.id);
        }
    }
    clearPendingPropDeletesMissingFromRemote(scope, remotePropIds);
}

function mergeEntitiesWithPendingLocal<T extends { id: string }>(
    scope: ProjectScope,
    remoteItems: T[],
    localItems: T[],
    isPendingWrite: (scope: ProjectScope, id: string) => boolean,
    isPendingDelete: (scope: ProjectScope, id: string) => boolean
): T[] {
    const remoteIds = new Set(remoteItems.map(item => item.id));
    const localById = new Map(localItems.map(item => [item.id, item]));
    const visibleRemoteItems = remoteItems
        .filter(item => !isPendingDelete(scope, item.id))
        .map(item => {
            const localItem = localById.get(item.id);
            if (localItem && isPendingWrite(scope, item.id)) {
                return localItem;
            }
            return item;
        });
    const pendingLocalItems = localItems.filter(item => (
        !remoteIds.has(item.id) && isPendingWrite(scope, item.id)
    ));

    return [...visibleRemoteItems, ...pendingLocalItems];
}

function clearAcknowledgedPendingEntityWrites<T extends { id: string }>(
    scope: ProjectScope,
    remoteItems: T[],
    localItems: T[],
    clearPendingWrite: (scope: ProjectScope, id: string) => void
): void {
    const localById = new Map(localItems.map(item => [item.id, item]));

    for (const remoteItem of remoteItems) {
        const localItem = localById.get(remoteItem.id);
        if (!localItem || recordsMatchForPendingWrite(remoteItem, localItem)) {
            clearPendingWrite(scope, remoteItem.id);
        }
    }
}

export function mergeSetsWithPendingLocal(
    scope: ProjectScope,
    remoteSets: AppSet[],
    localSets: AppSet[]
): AppSet[] {
    return mergeEntitiesWithPendingLocal(
        scope,
        remoteSets,
        localSets,
        isPendingSetWrite,
        isPendingSetDelete
    );
}

function clearAcknowledgedPendingSetWrites(
    scope: ProjectScope,
    remoteSets: AppSet[],
    localSets: AppSet[]
): void {
    clearAcknowledgedPendingEntityWrites(scope, remoteSets, localSets, clearPendingSetWrite);
    clearPendingSetDeletesMissingFromRemote(scope, new Set(remoteSets.map(setModel => setModel.id)));
}

function isNeverPendingDelete(): boolean {
    return false;
}

export function mergeCueAnchorsWithPendingLocal(
    scope: ProjectScope,
    remoteAnchors: AppCueAnchor[],
    localAnchors: AppCueAnchor[]
): AppCueAnchor[] {
    return mergeEntitiesWithPendingLocal(
        scope,
        remoteAnchors,
        localAnchors,
        isPendingCueAnchorWrite,
        isPendingCueAnchorDelete
    );
}

function clearAcknowledgedPendingCueAnchorWrites(
    scope: ProjectScope,
    remoteAnchors: AppCueAnchor[],
    localAnchors: AppCueAnchor[]
): void {
    clearAcknowledgedPendingEntityWrites(scope, remoteAnchors, localAnchors, clearPendingCueAnchorWrite);
    clearPendingCueAnchorDeletesMissingFromRemote(scope, new Set(remoteAnchors.map(anchor => anchor.id)));
}

export function mergeCustomCuesWithPendingLocal(
    scope: ProjectScope,
    remoteCues: AppCustomCue[],
    localCues: AppCustomCue[]
): AppCustomCue[] {
    return mergeEntitiesWithPendingLocal(
        scope,
        remoteCues,
        localCues,
        isPendingCustomCueWrite,
        isNeverPendingDelete
    );
}

function clearAcknowledgedPendingCustomCueWrites(
    scope: ProjectScope,
    remoteCues: AppCustomCue[],
    localCues: AppCustomCue[]
): void {
    clearAcknowledgedPendingEntityWrites(scope, remoteCues, localCues, clearPendingCustomCueWrite);
}

export function mergeSoundClipsWithPendingLocal(
    scope: ProjectScope,
    remoteClips: AppSoundClip[],
    localClips: AppSoundClip[]
): AppSoundClip[] {
    return mergeEntitiesWithPendingLocal(
        scope,
        remoteClips,
        localClips,
        isPendingSoundClipWrite,
        isPendingSoundClipDelete
    );
}

function clearAcknowledgedPendingSoundClipWrites(
    scope: ProjectScope,
    remoteClips: AppSoundClip[],
    localClips: AppSoundClip[]
): void {
    clearAcknowledgedPendingEntityWrites(scope, remoteClips, localClips, clearPendingSoundClipWrite);
    clearPendingSoundClipDeletesMissingFromRemote(scope, new Set(remoteClips.map(clip => clip.id)));
}

export function mergeSoundCuesWithPendingLocal(
    scope: ProjectScope,
    remoteCues: AppSoundCue[],
    localCues: AppSoundCue[]
): AppSoundCue[] {
    return mergeEntitiesWithPendingLocal(
        scope,
        remoteCues,
        localCues,
        isPendingSoundCueWrite,
        isPendingSoundCueDelete
    );
}

function clearAcknowledgedPendingSoundCueWrites(
    scope: ProjectScope,
    remoteCues: AppSoundCue[],
    localCues: AppSoundCue[]
): void {
    clearAcknowledgedPendingEntityWrites(scope, remoteCues, localCues, clearPendingSoundCueWrite);
    clearPendingSoundCueDeletesMissingFromRemote(scope, new Set(remoteCues.map(cue => cue.id)));
}

export function mergeMicAssignmentsWithPendingLocal(
    scope: ProjectScope,
    remoteAssignments: AppMicAssignment[],
    localAssignments: AppMicAssignment[]
): AppMicAssignment[] {
    return mergeEntitiesWithPendingLocal(
        scope,
        remoteAssignments,
        localAssignments,
        isPendingMicAssignmentWrite,
        isPendingMicAssignmentDelete
    );
}

function clearAcknowledgedPendingMicAssignmentWrites(
    scope: ProjectScope,
    remoteAssignments: AppMicAssignment[],
    localAssignments: AppMicAssignment[]
): void {
    clearAcknowledgedPendingEntityWrites(
        scope,
        remoteAssignments,
        localAssignments,
        clearPendingMicAssignmentWrite
    );
    clearPendingMicAssignmentDeletesMissingFromRemote(
        scope,
        new Set(remoteAssignments.map(assignment => assignment.id))
    );
}

export function mergeMicSwapsWithPendingLocal(
    scope: ProjectScope,
    remoteSwaps: AppMicSwap[],
    localSwaps: AppMicSwap[]
): AppMicSwap[] {
    return mergeEntitiesWithPendingLocal(
        scope,
        remoteSwaps,
        localSwaps,
        isPendingMicSwapWrite,
        isPendingMicSwapDelete
    );
}

function clearAcknowledgedPendingMicSwapWrites(
    scope: ProjectScope,
    remoteSwaps: AppMicSwap[],
    localSwaps: AppMicSwap[]
): void {
    clearAcknowledgedPendingEntityWrites(scope, remoteSwaps, localSwaps, clearPendingMicSwapWrite);
    clearPendingMicSwapDeletesMissingFromRemote(scope, new Set(remoteSwaps.map(swap => swap.id)));
}

export function mergeMicPackConfigWithPendingLocal(
    scope: ProjectScope,
    remoteConfig: AppMicPackConfig,
    localConfig: AppMicPackConfig
): AppMicPackConfig {
    if (isPendingMicPackConfigWrite(scope)) {
        return localConfig;
    }
    return remoteConfig;
}

function clearAcknowledgedPendingMicPackConfigWrite(
    scope: ProjectScope,
    remoteConfig: AppMicPackConfig,
    localConfig: AppMicPackConfig
): void {
    if (recordsMatchForPendingWrite(remoteConfig, localConfig)) {
        clearPendingMicPackConfigWrite(scope);
    }
}

export function mergeFlyCuesWithPendingLocal(
    scope: ProjectScope,
    remoteCues: AppFlyCue[],
    localCues: AppFlyCue[]
): AppFlyCue[] {
    const remoteIds = new Set(remoteCues.map(cue => cue.id));
    const localById = new Map(localCues.map(cue => [cue.id, cue]));
    const mergedRemoteCues = remoteCues.map(cue => {
        const localCue = localById.get(cue.id);
        if (localCue && isPendingFlyCueWrite(scope, cue.id)) {
            return localCue;
        }
        return cue;
    });
    const pendingLocalCues = localCues.filter(cue => (
        !remoteIds.has(cue.id) && isPendingFlyCueWrite(scope, cue.id)
    ));

    return [...mergedRemoteCues, ...pendingLocalCues];
}

function clearAcknowledgedPendingFlyCueWrites(
    scope: ProjectScope,
    remoteCues: AppFlyCue[],
    localCues: AppFlyCue[]
): void {
    const localById = new Map(localCues.map(cue => [cue.id, cue]));

    for (const remoteCue of remoteCues) {
        const localCue = localById.get(remoteCue.id);
        if (!localCue || recordsMatchForPendingWrite(remoteCue, localCue)) {
            clearPendingFlyCueWrite(scope, remoteCue.id);
        }
    }
}

export function mergeProjectionCuesWithPendingLocal(
    scope: ProjectScope,
    remoteCues: AppProjectionCue[],
    localCues: AppProjectionCue[]
): AppProjectionCue[] {
    const remoteIds = new Set(remoteCues.map(cue => cue.id));
    const localById = new Map(localCues.map(cue => [cue.id, cue]));
    const mergedRemoteCues = remoteCues.map(cue => {
        const localCue = localById.get(cue.id);
        if (localCue && isPendingProjectionCueWrite(scope, cue.id)) {
            return localCue;
        }
        return cue;
    });
    const pendingLocalCues = localCues.filter(cue => (
        !remoteIds.has(cue.id) && isPendingProjectionCueWrite(scope, cue.id)
    ));

    return [...mergedRemoteCues, ...pendingLocalCues];
}

function clearAcknowledgedPendingProjectionCueWrites(
    scope: ProjectScope,
    remoteCues: AppProjectionCue[],
    localCues: AppProjectionCue[]
): void {
    const localById = new Map(localCues.map(cue => [cue.id, cue]));

    for (const remoteCue of remoteCues) {
        const localCue = localById.get(remoteCue.id);
        if (!localCue || recordsMatchForPendingWrite(remoteCue, localCue)) {
            clearPendingProjectionCueWrite(scope, remoteCue.id);
        }
    }
}

export function mergeLightingCuesWithPendingLocal(
    scope: ProjectScope,
    remoteCues: AppLightingCue[],
    localCues: AppLightingCue[]
): AppLightingCue[] {
    const remoteIds = new Set(remoteCues.map(cue => cue.id));
    const localById = new Map(localCues.map(cue => [cue.id, cue]));
    const mergedRemoteCues = remoteCues.map(cue => {
        const localCue = localById.get(cue.id);
        if (localCue && isPendingLightingCueWrite(scope, cue.id)) {
            return localCue;
        }
        return cue;
    });
    const pendingLocalCues = localCues.filter(cue => (
        !remoteIds.has(cue.id) && isPendingLightingCueWrite(scope, cue.id)
    ));

    return [...mergedRemoteCues, ...pendingLocalCues];
}

export function mergeFollowSpotCuesWithPendingLocal(
    scope: ProjectScope,
    remoteCues: AppFollowSpotCue[],
    localCues: AppFollowSpotCue[]
): AppFollowSpotCue[] {
    const remoteIds = new Set(remoteCues.map(cue => cue.id));
    const localById = new Map(localCues.map(cue => [cue.id, cue]));
    const mergedRemoteCues = remoteCues.map(cue => {
        const localCue = localById.get(cue.id);
        if (localCue && isPendingFollowSpotCueWrite(scope, cue.id)) {
            return localCue;
        }
        return cue;
    });
    const pendingLocalCues = localCues.filter(cue => (
        !remoteIds.has(cue.id) && isPendingFollowSpotCueWrite(scope, cue.id)
    ));

    return [...mergedRemoteCues, ...pendingLocalCues];
}

export function mergeFollowSpotsWithPendingLocal(
    scope: ProjectScope,
    remoteSpots: AppFollowSpot[],
    localSpots: AppFollowSpot[]
): AppFollowSpot[] {
    const remoteIds = new Set(remoteSpots.map(spot => spot.id));
    const localById = new Map(localSpots.map(spot => [spot.id, spot]));
    const visibleRemoteSpots = remoteSpots
        .filter(spot => !isPendingFollowSpotDelete(scope, spot.id))
        .map(spot => {
            const localSpot = localById.get(spot.id);
            if (localSpot && isPendingFollowSpotWrite(scope, spot.id)) {
                return localSpot;
            }
            return spot;
        });
    const pendingLocalSpots = localSpots.filter(spot => (
        !remoteIds.has(spot.id) && isPendingFollowSpotWrite(scope, spot.id)
    ));

    return [...visibleRemoteSpots, ...pendingLocalSpots];
}

function clearAcknowledgedPendingLightingCueWrites(
    scope: ProjectScope,
    remoteCues: AppLightingCue[],
    localCues: AppLightingCue[]
): void {
    const localById = new Map(localCues.map(cue => [cue.id, cue]));
    for (const remoteCue of remoteCues) {
        const localCue = localById.get(remoteCue.id);
        if (!localCue || recordsMatchForPendingWrite(remoteCue, localCue)) {
            clearPendingLightingCueWrite(scope, remoteCue.id);
        }
    }
}

function clearAcknowledgedPendingFollowSpotCueWrites(
    scope: ProjectScope,
    remoteCues: AppFollowSpotCue[],
    localCues: AppFollowSpotCue[]
): void {
    const localById = new Map(localCues.map(cue => [cue.id, cue]));
    for (const remoteCue of remoteCues) {
        const localCue = localById.get(remoteCue.id);
        if (!localCue || recordsMatchForPendingWrite(remoteCue, localCue)) {
            clearPendingFollowSpotCueWrite(scope, remoteCue.id);
        }
    }
}

function clearAcknowledgedPendingFollowSpotWrites(
    scope: ProjectScope,
    remoteSpots: AppFollowSpot[],
    localSpots: AppFollowSpot[]
): void {
    const localById = new Map(localSpots.map(spot => [spot.id, spot]));
    const remoteSpotIds = new Set(remoteSpots.map(spot => spot.id));

    for (const remoteSpot of remoteSpots) {
        const localSpot = localById.get(remoteSpot.id);
        if (!localSpot || recordsMatchForPendingWrite(remoteSpot, localSpot)) {
            clearPendingFollowSpotWrite(scope, remoteSpot.id);
        }
    }
    clearPendingFollowSpotDeletesMissingFromRemote(scope, remoteSpotIds);
}

export function shouldSkipPreloadedResourceSnapshot(
    preloadedResources: Set<string>,
    resourceId?: string,
): boolean {
    if (!resourceId || !preloadedResources.has(resourceId)) return false;
    preloadedResources.delete(resourceId);
    return true;
}

export function mergeShowStructureForCloudSnapshot(
    incoming: Partial<AppShowStructure>,
    current: AppShowStructure,
): AppShowStructure {
    const currentBlocks = current.scriptBlocks ?? [];
    const incomingBlocks = incoming.scriptBlocks;
    const incomingBlocksHaveBetterLinks = Array.isArray(incomingBlocks)
        && incomingBlocks.length > 0
        && countBlockStructureLinks(incomingBlocks) > countBlockStructureLinks(currentBlocks);
    const useIncomingBlocks = Array.isArray(incomingBlocks)
        && incomingBlocks.length > 0
        && (currentBlocks.length === 0 || incomingBlocksHaveBetterLinks);

    return {
        acts: incoming.acts ?? current.acts ?? [],
        scenes: incoming.scenes ?? current.scenes ?? [],
        characters: incoming.characters ?? current.characters ?? [],
        scriptBlocks: useIncomingBlocks ? incomingBlocks : currentBlocks,
        ...(incoming.characterHints ? { characterHints: incoming.characterHints } : {}),
    };
}

function countBlockStructureLinks(blocks: AppScriptBlocks): number {
    return blocks?.reduce((total, block) => (
        total + (block.actId ? 1 : 0) + (block.sceneId ? 1 : 0)
    ), 0) ?? 0;
}

export function shouldSaveScriptBlocksForAutosave(
    currentBlocks: AppScriptBlocks,
    previousBlocks: AppScriptBlocks,
): boolean {
    if (!currentBlocks?.length) return false;
    if (currentBlocks === previousBlocks) return false;
    if (!previousBlocks?.length) return false;
    return true;
}

export type RemoteProjectUpdate = RemoteProjectUpdateWithControls;
export type RemoteProjectStoreUpdate = Omit<RemoteProjectUpdate, 'projectMeta'> & {
    projectMeta?: AppProjectMeta;
};
export type RemoteUpdateContext = {
    projectScope: ProjectScope;
    stateGetter: () => AppStateSnapshot;
    pendingGlobalFields: Set<PendingGlobalCloudField>;
    applyCloudState: (updates: Partial<CloudConnectionState>) => void;
};

function mergeGlobalRemoteFields(
    data: RemoteProjectUpdate,
    stateUpdate: RemoteProjectStoreUpdate,
    context: RemoteUpdateContext,
): void {
    if (data.showStructure) {
        const currentShowStructure = context.stateGetter().showStructure;
        const mergedShowStructure = mergeShowStructureForCloudSnapshot(data.showStructure, currentShowStructure);
        clearAcknowledgedPendingGlobalCloudField(
            context.pendingGlobalFields,
            'showStructure',
            cloudComparableShowStructure(mergedShowStructure),
            cloudComparableShowStructure(currentShowStructure),
        );
        stateUpdate.showStructure = mergeGlobalCloudFieldWithPendingLocal(
            context.pendingGlobalFields,
            'showStructure',
            mergedShowStructure,
            currentShowStructure,
        );
    }

    if (data.personnel) {
        const currentPersonnel = context.stateGetter().personnel ?? [];
        const linkedPersonnel = applyProjectMemberPersonnelLinks(
            data.personnel,
            context.stateGetter().admin.members,
        );
        clearPendingPersonnelProfileDeletesMissingFromRemote(
            context.projectScope,
            new Set(linkedPersonnel.map(person => person.id)),
        );
        const visiblePersonnel = mergePersonnelWithPendingLocal(context.projectScope, linkedPersonnel);
        clearAcknowledgedPendingGlobalCloudField(
            context.pendingGlobalFields,
            'personnel',
            visiblePersonnel,
            currentPersonnel,
        );
        stateUpdate.personnel = mergeGlobalCloudFieldWithPendingLocal(
            context.pendingGlobalFields,
            'personnel',
            visiblePersonnel,
            currentPersonnel,
        );
    }

    if (data.projectMeta) {
        const currentMeta = context.stateGetter().projectMeta;
        const mergedMeta = { ...currentMeta, ...data.projectMeta };
        clearAcknowledgedPendingGlobalCloudField(
            context.pendingGlobalFields,
            'projectMeta',
            data.projectMeta,
            projectMetaComparableForCloudSnapshot(data.projectMeta, currentMeta),
        );
        stateUpdate.projectMeta = mergeGlobalCloudFieldWithPendingLocal(
            context.pendingGlobalFields,
            'projectMeta',
            mergedMeta,
            currentMeta,
        );
    }

    if (data.rows) {
        const currentRows = context.stateGetter().rows ?? [];
        clearAcknowledgedPendingGlobalCloudField(context.pendingGlobalFields, 'rows', data.rows, currentRows);
        stateUpdate.rows = mergeGlobalCloudFieldWithPendingLocal(
            context.pendingGlobalFields,
            'rows',
            data.rows,
            currentRows,
        );
    }

    if (data.castAssignments) {
        const currentCastAssignments = context.stateGetter().castAssignments ?? [];
        clearAcknowledgedPendingGlobalCloudField(
            context.pendingGlobalFields,
            'castAssignments',
            data.castAssignments,
            currentCastAssignments,
        );
        stateUpdate.castAssignments = mergeGlobalCloudFieldWithPendingLocal(
            context.pendingGlobalFields,
            'castAssignments',
            data.castAssignments,
            currentCastAssignments,
        );
    }

    if (data.assets) {
        stateUpdate.assets = data.assets;
    }
}

function mergeBasicEntityRemoteFields(
    data: RemoteProjectUpdate,
    stateUpdate: RemoteProjectStoreUpdate,
    context: RemoteUpdateContext,
): void {
    if (data.props) {
        const currentProps = context.stateGetter().props ?? [];
        clearAcknowledgedPendingPropWrites(context.projectScope, data.props, currentProps);
        stateUpdate.props = mergePropsWithPendingLocal(context.projectScope, data.props, currentProps);
    }

    if (data.sets) {
        const currentSets = context.stateGetter().sets ?? [];
        clearAcknowledgedPendingSetWrites(context.projectScope, data.sets, currentSets);
        stateUpdate.sets = mergeSetsWithPendingLocal(context.projectScope, data.sets, currentSets);
    }
}

function mergeSoundRemoteFields(
    data: RemoteProjectUpdate,
    stateUpdate: RemoteProjectStoreUpdate,
    context: RemoteUpdateContext,
): void {
    if (data.soundClips) {
        const currentSoundClips = context.stateGetter().soundClips ?? [];
        clearAcknowledgedPendingSoundClipWrites(context.projectScope, data.soundClips, currentSoundClips);
        stateUpdate.soundClips = mergeSoundClipsWithPendingLocal(
            context.projectScope,
            data.soundClips,
            currentSoundClips,
        );
    }

    if (data.soundCues) {
        const currentSoundCues = context.stateGetter().soundCues ?? [];
        clearAcknowledgedPendingSoundCueWrites(context.projectScope, data.soundCues, currentSoundCues);
        stateUpdate.soundCues = mergeSoundCuesWithPendingLocal(context.projectScope, data.soundCues, currentSoundCues);
    }

    if (data.micAssignments) {
        const currentMicAssignments = context.stateGetter().micAssignments ?? [];
        clearAcknowledgedPendingMicAssignmentWrites(context.projectScope, data.micAssignments, currentMicAssignments);
        stateUpdate.micAssignments = mergeMicAssignmentsWithPendingLocal(
            context.projectScope,
            data.micAssignments,
            currentMicAssignments,
        );
    }

    if (data.micSwaps) {
        const currentMicSwaps = context.stateGetter().micSwaps ?? [];
        clearAcknowledgedPendingMicSwapWrites(context.projectScope, data.micSwaps, currentMicSwaps);
        stateUpdate.micSwaps = mergeMicSwapsWithPendingLocal(context.projectScope, data.micSwaps, currentMicSwaps);
    }

    if (data.micPackConfig) {
        const currentMicPackConfig = context.stateGetter().micPackConfig;
        clearAcknowledgedPendingMicPackConfigWrite(context.projectScope, data.micPackConfig, currentMicPackConfig);
        stateUpdate.micPackConfig = mergeMicPackConfigWithPendingLocal(
            context.projectScope,
            data.micPackConfig,
            currentMicPackConfig,
        );
    }
}

function mergePromptBookRemoteFields(
    data: RemoteProjectUpdate,
    stateUpdate: RemoteProjectStoreUpdate,
    context: RemoteUpdateContext,
): void {
    if (data.cueAnchors) {
        const currentCueAnchors = context.stateGetter().cueAnchors ?? [];
        clearAcknowledgedPendingCueAnchorWrites(context.projectScope, data.cueAnchors, currentCueAnchors);
        stateUpdate.cueAnchors = mergeCueAnchorsWithPendingLocal(
            context.projectScope,
            data.cueAnchors,
            currentCueAnchors,
        );
    }

    if (data.customCues) {
        const currentCustomCues = context.stateGetter().customCues ?? [];
        clearAcknowledgedPendingCustomCueWrites(context.projectScope, data.customCues, currentCustomCues);
        stateUpdate.customCues = mergeCustomCuesWithPendingLocal(
            context.projectScope,
            data.customCues,
            currentCustomCues,
        );
    }

    if (data.flyCues) {
        const currentFlyCues = context.stateGetter().flyCues ?? [];
        clearAcknowledgedPendingFlyCueWrites(context.projectScope, data.flyCues, currentFlyCues);
        stateUpdate.flyCues = mergeFlyCuesWithPendingLocal(context.projectScope, data.flyCues, currentFlyCues);
    }

    if (data.projectionCues) {
        const currentProjectionCues = context.stateGetter().projectionCues ?? [];
        clearAcknowledgedPendingProjectionCueWrites(context.projectScope, data.projectionCues, currentProjectionCues);
        stateUpdate.projectionCues = mergeProjectionCuesWithPendingLocal(
            context.projectScope,
            data.projectionCues,
            currentProjectionCues,
        );
    }
}

function mergeLightingRemoteFields(
    data: RemoteProjectUpdate,
    stateUpdate: RemoteProjectStoreUpdate,
    context: RemoteUpdateContext,
): void {
    if (data.lightingCues) {
        const currentLightingCues = context.stateGetter().lightingCues ?? [];
        clearAcknowledgedPendingLightingCueWrites(context.projectScope, data.lightingCues, currentLightingCues);
        stateUpdate.lightingCues = mergeLightingCuesWithPendingLocal(
            context.projectScope,
            data.lightingCues,
            currentLightingCues,
        );
    }

    if (data.followSpotCues) {
        const currentFollowSpotCues = context.stateGetter().followSpotCues ?? [];
        clearAcknowledgedPendingFollowSpotCueWrites(context.projectScope, data.followSpotCues, currentFollowSpotCues);
        stateUpdate.followSpotCues = mergeFollowSpotCuesWithPendingLocal(
            context.projectScope,
            data.followSpotCues,
            currentFollowSpotCues,
        );
    }

    if (data.followSpots) {
        const currentFollowSpots = context.stateGetter().followSpots ?? [];
        clearAcknowledgedPendingFollowSpotWrites(context.projectScope, data.followSpots, currentFollowSpots);
        stateUpdate.followSpots = mergeFollowSpotsWithPendingLocal(
            context.projectScope,
            data.followSpots,
            currentFollowSpots,
        );
    }
}

function mergeEntityRemoteFields(
    data: RemoteProjectUpdate,
    stateUpdate: RemoteProjectStoreUpdate,
    context: RemoteUpdateContext,
): void {
    mergeBasicEntityRemoteFields(data, stateUpdate, context);
    mergeSoundRemoteFields(data, stateUpdate, context);
    mergePromptBookRemoteFields(data, stateUpdate, context);
    mergeLightingRemoteFields(data, stateUpdate, context);
}
export function buildRemoteProjectStoreUpdate(
    context: RemoteUpdateContext & { data: RemoteProjectUpdate },
): RemoteProjectStoreUpdate {
    const stateUpdate = { ...context.data } as RemoteProjectStoreUpdate;
    delete stateUpdate.notes;

    const mergeContext: RemoteUpdateContext = {
        projectScope: context.projectScope,
        stateGetter: context.stateGetter,
        pendingGlobalFields: context.pendingGlobalFields,
        applyCloudState: context.applyCloudState,
    };

    mergeGlobalRemoteFields(stateUpdate, stateUpdate, mergeContext);
    mergeEntityRemoteFields(stateUpdate, stateUpdate, mergeContext);
    applySubscriptionControlFields(stateUpdate, {
        stateGetter: mergeContext.stateGetter,
        safeSetCloudState: mergeContext.applyCloudState,
    });

    return stateUpdate;
}
